import { useEffect, useState } from 'react';
import { controlService, type ControlAreaStatus } from '../services/controlService';
import { useSteuerungEnabled } from '../store/featureStore';

const POLL_MS = 60_000;

/**
 * § 14a status per zone for the dashboard cards (STEUERUNG.md 7.2 Nr. 17): the Netzbereich row of every zone, keyed by
 * zone id. Empty while the control module is not booked or the status is not loaded yet.
 */
export function useControlAreaStatusByZone(): Map<string, ControlAreaStatus> {
  const enabled = useSteuerungEnabled();
  const [byZone, setByZone] = useState<Map<string, ControlAreaStatus>>(new Map());

  useEffect(() => {
    if (!enabled) {
      setByZone(new Map());
      return undefined;
    }

    let cancelled = false;
    let lastJson = '';
    const load = async () => {
      try {
        const rows = await controlService.getAreaStatus();
        if (cancelled) return;
        // new objects every poll would re-render every (memoised) zone card - only replace on a real change
        const json = JSON.stringify(rows);
        if (json === lastJson) return;
        lastJson = json;
        const next = new Map<string, ControlAreaStatus>();
        for (const row of rows) {
          for (const zoneId of row.zoneIds ?? []) next.set(zoneId, row);
        }
        setByZone(next);
      } catch {
        // no stale "steuerbar" on the cards: without the status the cards show none
        if (cancelled) return;
        lastJson = '';
        setByZone(new Map());
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled]);

  return byZone;
}
